import { Outlet, useMatch, useMatches } from "@tanstack/react-router";
import { useState } from "react";

import { Shell } from "./shell/Shell";
import { LastTeamFrame } from "./TeamLayout";
import { frameOf } from "./ui-switch/frame";
import { UploadsProvider } from "./uploads/UploadsProvider";

/**
 * The frame of every signed-in page. A team's pages render the shell in TeamLayout, which owns
 * the team's stream and gives the shell its sidebar. A page that asks for a team's frame
 * (`staticData.teamFrame`, the staff pages) gets the frame of the team this tab was last on, so
 * the sidebar it was opened from stays, or Pro's or Simple's frame when its `?ui=` names one; the
 * others get the header alone here.
 */
export function AppLayout() {
  // The team route's own param: a staff page's `teamId` is the team it inspects, not its frame.
  const ownFrame = useMatches({ select: (matches) => matches.some((match) => match.staticData.ownFrame === true) });
  const urlTeamId = useMatch({
    from: "/signed-in/teams/$teamId",
    shouldThrow: false,
    select: (match) => match.params.teamId,
  });
  const urlBrandId = useMatch({
    from: "/signed-in/teams/$teamId/brands/$brandId",
    shouldThrow: false,
    select: (match) => match.params.brandId,
  });
  // A page with no team of its own in a team's frame, and the UI it is drawn in.
  const teamFrame = useMatches({
    select: (matches) => {
      const page = matches.find((match) => match.staticData.teamFrame === true);
      return page === undefined ? undefined : frameOf(page.search);
    },
  });
  // The last team page's team and brand, kept for a page with no team of its own.
  const [lastTeamId, setLastTeamId] = useState(urlTeamId);
  if (urlTeamId !== undefined && urlTeamId !== lastTeamId) setLastTeamId(urlTeamId);
  const [lastBrandId, setLastBrandId] = useState(urlBrandId);
  if (urlBrandId !== undefined && urlBrandId !== lastBrandId) setLastBrandId(urlBrandId);
  // The app's upload queue and its tray, above every page (spec upload-tray).
  return (
    <UploadsProvider>
      {urlTeamId !== undefined || ownFrame ? (
        <Outlet />
      ) : teamFrame !== undefined ? (
        <LastTeamFrame teamId={lastTeamId} brandId={lastBrandId} ui={teamFrame} />
      ) : (
        <Shell>
          <Outlet />
        </Shell>
      )}
    </UploadsProvider>
  );
}
